import { WEB_CLIENT_ID } from './google.config.web';
import { beginSocialRedirect, shouldUseSocialRedirect } from './socialRedirect.web';

declare global {
	interface Window {
		google?: any;
	}
}

function ensureGsiLoaded(): Promise<void> {
	if (typeof window === 'undefined') {
		return Promise.reject(new Error('No window (SSR)'));
	}

	if (window.google?.accounts?.id) return Promise.resolve();

	return new Promise((resolve, reject) => {
		const src = 'https://accounts.google.com/gsi/client';
		const existing = document.querySelector(`script[src="${src}"]`) as HTMLScriptElement | null;

		if (existing) {
			// script exists; may already be loaded
			if (window.google?.accounts?.id) return resolve();

			existing.addEventListener('load', () => resolve(), { once: true });
			existing.addEventListener(
				'error',
				() => reject(new Error('Failed to load Google GSI script')),
				{ once: true },
			);
			return;
		}

		const s = document.createElement('script');
		s.src = src;
		s.async = true;
		s.defer = true;
		s.onload = () => resolve();
		s.onerror = () => reject(new Error('Failed to load Google GSI script'));
		document.head.appendChild(s);
	});
}

type GoogleCredentialHandler = (credential: string) => void;

/** What Google draws. Defaults to the round icon button; a wide one takes the container's width. */
export type GoogleButtonOptions = {
	variant?: 'icon' | 'wide';
	/** For `wide`: the button's width in px (Google caps it at 400). */
	width?: number;
};

export async function renderGoogleSignInButton(
	container: HTMLElement,
	onCredential: GoogleCredentialHandler,
	options: GoogleButtonOptions = {},
) {
	await ensureGsiLoaded();

	const googleAny = window.google;
	if (!googleAny?.accounts?.id) {
		throw new Error('window.google.accounts.id is not available');
	}

	container.replaceChildren();

	googleAny.accounts.id.initialize({
		client_id: WEB_CLIENT_ID,
		auto_select: false,
		callback: (resp: any) => {
			if (resp?.error) {
				console.error('[GoogleSignIn:web] Google sign-in error:', resp.error);
				return;
			}

			if (!resp?.credential) {
				console.error('[GoogleSignIn:web] No credential returned from Google');
				return;
			}

			onCredential(resp.credential);
		},
	});

	googleAny.accounts.id.renderButton(
		container,
		options.variant === 'wide'
			? {
					type: 'standard',
					theme: 'outline',
					size: 'large',
					text: 'continue_with',
					shape: 'rectangular',
					width: Math.min(400, Math.round(options.width ?? 320)),
				}
			: {
					type: 'icon',
					theme: 'outline',
					size: 'large',
					shape: 'circle',
				},
	);
}

export default async function googleSignInWeb(): Promise<string> {
	// The installed app cannot get Google's popup answer back; the page goes to Google
	// instead and the login resumes from the return URL on the next boot.
	if (shouldUseSocialRedirect()) {
		return beginSocialRedirect('google');
	}
	throw new Error('Google web sign-in must use the rendered Google button');
}
